import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  signal,
  ViewChild,
} from "@angular/core";
import { BehaviorSubject, catchError, Observable, of } from "rxjs";
import { MenuService } from "@dmms/core/menu";
import { map, switchMap, tap } from "rxjs/operators";
import { IMenuItemModel } from "@models/core/menu";
import { IApiResponsePagination } from "@models/http/http-options";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { fromEvent } from "rxjs";
import { AuthService } from "@app/core/auth";

@Component({
  selector: "app-toolbar-menu",
  templateUrl: "./toolbar-menu.component.html",
  styleUrl: "./toolbar-menu.component.scss",
  standalone: false,
})
export class ToolbarMenuComponent implements OnChanges, AfterViewInit, OnDestroy {
  private readonly menuService = inject(MenuService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private syncScrollTimeout?: ReturnType<typeof setTimeout>;

  slug = input<string>();

  @ViewChild("scrollContainer")
  scrollContainer?: ElementRef<HTMLElement>;

  readonly slug$ = new BehaviorSubject<string>(this.slug());

  readonly items = signal<IMenuItemModel[] | null>(null);
  readonly hasOverflow = signal(false);
  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);
  readonly showLeftArrow = computed(
    () => this.hasOverflow() && this.canScrollLeft(),
  );
  readonly showRightArrow = computed(
    () => this.hasOverflow() && this.canScrollRight(),
  );

  readonly menu$: Observable<boolean> =
    this.authService.currentUser$.pipe(
      switchMap((user) => {
        if (!user) {
          this.items.set([]);
          return of(null);
        }
        return this.slug$.pipe(
          tap(() => this.items.set(null)),
          switchMap((slug) =>
            this.menuService
              .getMenuBySlug(slug)
              .pipe(catchError(() => of<IApiResponsePagination<IMenuItemModel[]> | null>(null))),
          ),
          tap((response) => {
            const responseItems = response?.value?.items?.length
              ? response.value.items
              : null;

            this.items.set(
              responseItems || [],
            );

            this.scheduleSyncScrollState();
          }),
        );
      }),
      map(() => true),
    );

  ngAfterViewInit() {
    this.scheduleSyncScrollState();

    fromEvent(window, "resize")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scheduleSyncScrollState());
  }

  ngOnChanges() {
    this.slug$.next(this.slug());
  }

  ngOnDestroy() {
    if (this.syncScrollTimeout) {
      clearTimeout(this.syncScrollTimeout);
    }
  }

  scrollRight() {
    const container = this.scrollContainer?.nativeElement;

    if (!container) {
      return;
    }

    const step = Math.max(container.clientWidth * 0.8, 220);

    container.scrollBy({
      left: step,
      behavior: "smooth",
    });

    this.scheduleSyncScrollState();
  }

  scrollLeft() {
    const container = this.scrollContainer?.nativeElement;

    if (!container) {
      return;
    }

    const step = Math.max(container.clientWidth * 0.8, 220);

    container.scrollBy({
      left: -step,
      behavior: "smooth",
    });

    this.scheduleSyncScrollState();
  }

  syncScrollState() {
    const container = this.scrollContainer?.nativeElement;

    if (!container) {
      this.hasOverflow.set(false);
      this.canScrollLeft.set(false);
      this.canScrollRight.set(false);
      return;
    }

    const hasOverflow = container.scrollWidth - container.clientWidth > 1;
    const canScrollLeft = container.scrollLeft > 1;
    const canScrollRight =
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1;

    this.hasOverflow.set(hasOverflow);
    this.canScrollLeft.set(canScrollLeft);
    this.canScrollRight.set(canScrollRight);
  }

  private scheduleSyncScrollState() {
    if (this.syncScrollTimeout) {
      clearTimeout(this.syncScrollTimeout);
    }

    this.syncScrollTimeout = setTimeout(() => this.syncScrollState(), 60);
  }
}
